import { Link } from 'react-router-dom';
import { DetailPanel, type PanelSpan } from '@/components/shared';
import type { BackofficeMessageBusSubscriberDetail } from '../types';

export interface MessageBusSubscriberDetailsTileProps {
  subscriber: BackofficeMessageBusSubscriberDetail;
  panelId?: string | undefined;
  currentSpan?: PanelSpan | undefined;
}

function DetailRow({
  label,
  value,
  href,
  compact,
}: {
  label: string;
  value: string;
  href?: string;
  compact: boolean;
}) {
  const content = href ? (
    <Link
      to={href}
      className="focus-ring min-w-0 rounded-sm font-medium text-neutral-900 hover:text-backoffice-600"
    >
      {value}
    </Link>
  ) : (
    <span className="min-w-0">{value}</span>
  );

  return (
    <div
      className={[
        'grid border-b border-neutral-100 last:border-b-0',
        compact ? 'gap-0.5 py-2' : 'gap-1 py-3 sm:grid-cols-[10rem_minmax(0,1fr)] sm:gap-4',
      ].join(' ')}
    >
      <dt
        className={
          compact ? 'text-[11px] font-semibold text-neutral-900' : 'font-semibold text-neutral-900'
        }
      >
        {label}
      </dt>
      <dd className={compact ? 'min-w-0 text-[11px] text-neutral-700' : 'min-w-0 text-neutral-700'}>
        {content}
      </dd>
    </div>
  );
}

function formatDateTime(value: string | null): string {
  return value ? new Date(value).toLocaleString() : '—';
}

export function MessageBusSubscriberDetailsTile({
  subscriber,
  panelId,
  currentSpan = '3x3',
}: MessageBusSubscriberDetailsTileProps) {
  const compact = currentSpan === '1x1';
  const topicHref = `/developer/messagebus/topics/${encodeURIComponent(subscriber.topicName)}`;
  const detailItems = [
    {
      id: 'topic',
      label: 'Topic',
      content: (
        <DetailRow
          key="topic"
          label="Topic"
          value={subscriber.topicName}
          href={topicHref}
          compact={compact}
        />
      ),
    },
    {
      id: 'subscriber',
      label: 'Subscriber',
      content: (
        <DetailRow
          key="subscriber"
          label="Subscriber"
          value={subscriber.subscriberName}
          compact={compact}
        />
      ),
    },
    {
      id: 'transfer-messages',
      label: 'Transfer messages',
      content: (
        <DetailRow
          key="transfer-messages"
          label="Transfer messages"
          value={
            subscriber.transferMessageCount === null ? '—' : String(subscriber.transferMessageCount)
          }
          compact={compact}
        />
      ),
    },
    {
      id: 'transfer-dead-letters',
      label: 'Transfer dead letters',
      content: (
        <DetailRow
          key="transfer-dead-letters"
          label="Transfer dead letters"
          value={
            subscriber.transferDeadLetterMessageCount === null
              ? '—'
              : String(subscriber.transferDeadLetterMessageCount)
          }
          compact={compact}
        />
      ),
    },
    {
      id: 'created-at',
      label: 'Created at',
      content: (
        <DetailRow
          key="created-at"
          label="Created at"
          value={formatDateTime(subscriber.createdAt)}
          compact={compact}
        />
      ),
    },
    {
      id: 'modified-at',
      label: 'Modified at',
      content: (
        <DetailRow
          key="modified-at"
          label="Modified at"
          value={formatDateTime(subscriber.modifiedAt)}
          compact={compact}
        />
      ),
    },
    {
      id: 'accessed-at',
      label: 'Accessed at',
      content: (
        <DetailRow
          key="accessed-at"
          label="Accessed at"
          value={formatDateTime(subscriber.accessedAt)}
          compact={compact}
        />
      ),
    },
  ];

  return (
    <DetailPanel
      title="Subscriber details"
      span="3x3"
      panelId={panelId}
      currentSpan={currentSpan}
      settingsItems={detailItems}
    />
  );
}
